import React, { useEffect, useMemo, useState } from 'react';
import useSpotConfig from '@host-panel/request/config/use-config';
import { SettingsTitle } from '../../components/SettingsTitle';
import { Badge } from 'app/scripts/components/common/badge';
import { Toggle } from 'app/scripts/components/common/toggle';
import { SettingsDescription } from '../../components/SettingsDescription';
import { ClickableDescriptionSettings } from '../../components/ClickableDescriptionSettings/ClickableDescriptionSettings';
import NumericInput from '../../../common/inputs/customs/num-input-add';

import styles from './FloatingActionUnit.module.scss';

const MAX_DESKTOP_OFFSET = 500;
const MIN_DESKTOP_OFFSET = 16;
const MAX_MOBILE_OFFSET = 500;
const MIN_MOBILE_OFFSET = 5;

const IS_FAU_ENABLED_KEY = 'tenant.conversation.floating_action_unit_enabled';
const FAU_OFFSET_KEY = 'tenant.conversation.floating_action_unit_bottom_offset';
const IS_MOBILE_FAU_ENABLED_KEY = 'tenant.conversation.floating_action_unit_mobile_enabled';
const FAU_MOBILE_OFFSET_KEY = 'tenant.conversation.floating_action_unit_mobile_bottom_offset';
const IS_FAU_EVENT_ENABLED_KEY = 'tenant.conversation.floating_action_unit_event_enabled';
const OW_DEVELOPERS_LINK_KEY = 'tenant.shared.ow_developers_link';

export function FloatingEngagingUnitSettings() {
  const [isDesktopEnabled, setIsDesktopEnabled] = useState(false);
  const [desktopOffset, setDesktopOffset] = useState(MIN_DESKTOP_OFFSET);
  const [isMobileEnabled, setIsMobileEnabled] = useState(false);
  const [mobileOffset, setMobileOffset] = useState(MIN_MOBILE_OFFSET);
  const [isFauEventEnabled, setIsFaueventEnabled] = useState(false);
  const [owDevelopersLink, setOwDevelopersLink] = useState('');

  const [configValues, setConfigs] = (useSpotConfig(
    [
      IS_FAU_ENABLED_KEY,
      FAU_OFFSET_KEY,
      IS_MOBILE_FAU_ENABLED_KEY,
      FAU_MOBILE_OFFSET_KEY,
      IS_FAU_EVENT_ENABLED_KEY,
      OW_DEVELOPERS_LINK_KEY
    ],
    {
      [IS_FAU_ENABLED_KEY]: false,
      [FAU_OFFSET_KEY]: MIN_DESKTOP_OFFSET,
      [IS_MOBILE_FAU_ENABLED_KEY]: false,
      [FAU_MOBILE_OFFSET_KEY]: MIN_MOBILE_OFFSET,
      [IS_FAU_EVENT_ENABLED_KEY]: false,
      [OW_DEVELOPERS_LINK_KEY]: ''
    }
  ) as unknown) as [[boolean, number, boolean, number, boolean, string], (value) => void];

  const setDesktopValidatedOffset = useMemo(
    () => createValidatedOffsetSetter(MIN_DESKTOP_OFFSET, MAX_DESKTOP_OFFSET, setDesktopOffset),
    []
  );

  const setMobileValidatedOffset = useMemo(
    () => createValidatedOffsetSetter(MIN_MOBILE_OFFSET, MAX_MOBILE_OFFSET, setMobileOffset),
    []
  );

  useEffect(() => {
    const [
      isDesktopEnabledConfig,
      desktopOffsetConfig,
      isMobileEnabledConfig,
      mobileOffsetConfig,
      isFaueventEnabledConfig,
      owDevelopersLink
    ] = configValues;

    setIsDesktopEnabled(isDesktopEnabledConfig);
    setDesktopValidatedOffset(desktopOffsetConfig);
    setIsMobileEnabled(isMobileEnabledConfig);
    setMobileValidatedOffset(mobileOffsetConfig);
    setIsFaueventEnabled(isFaueventEnabledConfig);
    setOwDevelopersLink(owDevelopersLink);
  }, [configValues, setDesktopValidatedOffset, setMobileValidatedOffset]);

  const onDesktopEnableChanged = (value: boolean) => {
    setIsDesktopEnabled(value);
    setConfigs({ key: IS_FAU_ENABLED_KEY, value });
  };

  const onDesktopSave = () => {
    const validOffset = setDesktopValidatedOffset(desktopOffset);

    setConfigs({ key: FAU_OFFSET_KEY, value: validOffset });
  };

  const onDesktopOffsetChange = (value: string) => {
    setDesktopOffset(parseInt(value));
  };

  const onMobileEnableChanged = (value: boolean) => {
    setIsMobileEnabled(value);
    setConfigs({ key: IS_MOBILE_FAU_ENABLED_KEY, value });
  };

  const onMobileSave = () => {
    const validOffset = setMobileValidatedOffset(mobileOffset);

    setConfigs({ key: FAU_MOBILE_OFFSET_KEY, value: validOffset });
  };

  const onMobileOffsetChange = (value: string) => {
    setMobileOffset(parseInt(value));
  };

  return (
    <div>
      <SettingsTitle
        title={
          isFauEventEnabled
            ? 'Floating Engagement Unit - Event Listener Implementation'
            : 'Floating Engagement Unit'
        }
        beforeTitle={<Badge label="New" type="outlined" color="#2778CE" />}
      />

      {isFauEventEnabled ? (
        <ClickableDescriptionSettings
          prefix="Boost conversation engagement with a floating bubble."
          linkText="Click here"
          suffix="to learn how to consume the event and trigger the floating unit."
          linkUrl={owDevelopersLink}
        />
      ) : (
        <SettingsDescription description="Boost engagement with a floating bubble to Indicate the number of comments and real-time typing." />
      )}

      <Toggle
        label={`${getStatus(isDesktopEnabled)} on Desktop`}
        checked={isDesktopEnabled}
        onChange={onDesktopEnableChanged}
      />
      <NumericInput
        description="Offset"
        onClick={onDesktopSave}
        onChange={onDesktopOffsetChange}
        disabledAddButton={!isDesktopEnabled}
        value={desktopOffset}
        disabled={!isDesktopEnabled}
      />
      <Toggle
        label={`${getStatus(isMobileEnabled)} on Mobile`}
        checked={isMobileEnabled}
        onChange={onMobileEnableChanged}
        className={styles.togglePadding}
      />
      <NumericInput
        description="Offset"
        onClick={onMobileSave}
        onChange={onMobileOffsetChange}
        disabledAddButton={!isMobileEnabled}
        value={mobileOffset}
        disabled={!isMobileEnabled}
      />
    </div>
  );
}

function createValidatedOffsetSetter(
  minOffset: number,
  maxOffset: number,
  setter: React.Dispatch<React.SetStateAction<number>>
) {
  return (value: number) => {
    let newValue = value;

    if (value > maxOffset) {
      newValue = maxOffset;
    } else if (value < minOffset) {
      newValue = minOffset;
    }

    setter(newValue);

    return newValue;
  };
}

function getStatus(isEnabled: boolean) {
  return isEnabled ? 'Enabled' : 'Disabled';
}
